<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateTemplate',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const formId = ref(null);
  const formName = ref('template');
  const item = ref(null);
  const objectTypes = ref(null);
  const templateTypes = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    objectTypes: objectTypes.value,
    templateTypes: templateTypes.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return $fn.value.schemaDefaults('CreateTemplate');
  };
  const $$load_objectTypes = () => {
    return $fn.value.getEnumValuesFromSchema('ObjectTypes');
  };
  const $$load_templateTypes = () => {
    return $fn.value.getEnumValuesFromSchema('TemplateTypes');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'item' },
      { name: 'objectTypes' },
      { name: 'templateTypes' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      objectTypes: $$load_objectTypes,
      templateTypes: $$load_templateTypes,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      item,
      objectTypes,
      templateTypes,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Templates', 'Create Template') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_2zawgxtnp_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_kjkx24sw7_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: $event.data.response.data,
      },
    };
  };
  const inst_evh_1040167445267876_success_7mpyt3teg_getWhen = async (
    $event
  ) => {
    return !!props.modalName;
  };
  const act_1040167445267876_request_bnit1163h_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_template',
      data: { ...item.value, content: '<div></div>' },
    };
  };

  const act_7315092382398562_closeModal_2zawgxtnp =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_2zawgxtnp_getActionArgs,
    });
  const inst_evh_7315092382398562_close_bcua5kese =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_2zawgxtnp,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_bcua5kese],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_mdc3iffe8 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_6pa0yswua =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_mdc3iffe8,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_6pa0yswua],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_0nuedpjvu =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_83o2uiqov =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_0nuedpjvu,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_83o2uiqov],
    event: 'close',
  });
  const act_1040167445267876_closeModal_kjkx24sw7 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_kjkx24sw7_getActionArgs,
    });
  const inst_evh_1040167445267876_success_7mpyt3teg =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_kjkx24sw7,
      event: 'success',
      when: inst_evh_1040167445267876_success_7mpyt3teg_getWhen,
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_bnit1163h = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_bnit1163h_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_7mpyt3teg],
  });
  const inst_evh_1040167445267876_submit_caqhuccnx =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_bnit1163h,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_caqhuccnx],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_ytk9jgpeh =
    new actionTypes.CloseModalAction({});
  const inst_evh_2434514588044777_clickPreventStop_q4s3l6ykt =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_ytk9jgpeh,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_q4s3l6ykt],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_bcua5kese,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_6pa0yswua,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_83o2uiqov,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_7mpyt3teg,
    inst_evh_1040167445267876_submit_caqhuccnx,
    submit: inst_evh_1040167445267876_submit_caqhuccnx,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_q4s3l6ykt,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_bcua5kese = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_bcua5kese.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_6pa0yswua = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_6pa0yswua.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_83o2uiqov = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_83o2uiqov.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_7mpyt3teg = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_7mpyt3teg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_caqhuccnx = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_caqhuccnx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_q4s3l6ykt = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_q4s3l6ykt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateTemplate"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateTemplate"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup name="name" :label="pgettext('Templates', 'Name')">
          <ItpFormInput
            name="name"
            size="md"
            v-model="item.name"
            required
            autofocus
            :placeholder="pgettext('Templates', 'Name of template')"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup name="name" :label="pgettext('Templates', 'Object type')">
          <ItpFormNativeSelect
            name="name"
            size="md"
            v-model="item.object_type"
            :options="objectTypes"
            required
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="template_type"
          :label="pgettext('Templates', 'Template type')"
          :description="
            pgettext(
              'Templates',
              'Emails are generated from MJML templates, PDF documents are generated from HTML templates.'
            )
          "
        >
          <ItpFormNativeSelect
            name="template_type"
            size="md"
            v-model="item.template_type"
            required
            :options="templateTypes"
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="formId"
          :text="pgettext('Templates', 'Save')"
          type="submit"
          variant="primary"
          default
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Templates', 'Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
